import React, { forwardRef } from 'react';
import { TextField, useTheme } from '@mui/material';
import { validateAndCleanEmojis } from '../utils';
import { useAlertContext } from '../contexts/AlertContext';
import { AlertType } from './DSAlert';
import { EMOJIS_NOT_ALLOWED_ALERT } from '../consts/alerts';

interface DSTextFieldProps extends React.ComponentProps<typeof TextField> {
  preventEmojis?: boolean;
}

const DSTextField = forwardRef<HTMLInputElement, DSTextFieldProps>(
  (props, ref) => {
    const { preventEmojis = true, onChange, ...restProps } = props;
    const { spacing, palette } = useTheme();
    const { showAlert } = useAlertContext();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let finalEvent = event;

      if (preventEmojis) {
        const { hasEmoji, cleanedValue } = validateAndCleanEmojis(
          event.target.value,
        );

        if (hasEmoji) {
          event.target.value = cleanedValue;

          showAlert({
            message: EMOJIS_NOT_ALLOWED_ALERT,
            alertType: AlertType.ERROR,
          });

          finalEvent = {
            ...event,
            target: { ...event.target, value: cleanedValue },
          } as React.ChangeEvent<HTMLInputElement>;
        }
      }

      if (onChange) {
        onChange(finalEvent);
      }
    };
    return (
      <TextField
        ref={ref}
        {...restProps}
        onChange={handleChange}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing(1, 2),
          borderRadius: '28px',
          background: 'rgba(255, 255, 255, 0.09)',
          '.MuiInputBase-input': {
            fontSize: spacing(2),
            padding: spacing(1),
            '&::placeholder': {
              color: palette.text.disabled,
            },
          },
          '.MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset',
            WebkitTextFillColor: palette.text.primary,
            caretColor: palette.text.primary,
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px transparent inset',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px transparent inset',
          },
          '& input:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 100px transparent inset',
          },
          ...restProps.sx,
        }}
      />
    );
  },
);

export default DSTextField;
